import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';

import { useQueryClient, useMutation } from '@sopranodesign/pulse-hooks';
import { AnimatePresence } from '@sopranodesign/framer-motion';
import {
  Button,
  PanelSlider,
  Flex,
  Label,
  TextArea,
  useNotification
} from '@sopranodesign/pulse-components';
import {
  ConditionalProvider,
  ConditionalTypeProvider
} from '@sopranodesign/pulse-conditionals';
import { PermissionFilter } from '@sopranodesign/pulse-auth';

import { conditionals } from '../../../subjects/index';
import {
  createConversation,
  createConversationAutoGenerate
} from '../api/index';
import ConversationDetails from '../../../Components/ConversationDetails';
import Modal from '../../Builder/Components/Modal';

const CreateConversation = () => {
  const [showCreate, setShowCreate] = useState(false);
  const [showAutoGenerate, setShowAutoGenerate] = useState(false);
  const [autoGeneratePrompt, setAutoGeneratePrompt] = useState('');

  const history = useHistory();

  const { showSuccess } = useNotification();

  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: createConversation,
    onSuccess: async ({ data }) => {
      await queryClient.invalidateQueries('conversations/builder');
      showSuccess('Successfully created your new workflow!');
      history.push(`/builder/${data.data.id}`);
    }
  });

  const autoGenerateMutation = useMutation({
    mutationFn: createConversationAutoGenerate,
    onSuccess: async ({ data }) => {
      await queryClient.invalidateQueries('conversations/builder');
      showSuccess('Successfully generated your new workflow!');
      setAutoGeneratePrompt('');
      setShowAutoGenerate(false);
      history.push(`/builder/${data.data.id}`);
    }
  });

  const handleSubmit = conversation => {
    mutation.mutate(conversation);
  };

  const handleAutoGenerateSubmit = event => {
    event.preventDefault();

    autoGenerateMutation.mutate(autoGeneratePrompt);
  };

  const handleAutoGenerateClose = () => {
    setAutoGeneratePrompt('');
    setShowAutoGenerate(false);
  };

  return (
    <>
      <AnimatePresence>
        {showCreate && (
          <PanelSlider
            width="56rem"
            header={`Create New Workflow`}
            handleHide={() => setShowCreate(false)}>
            <ConditionalTypeProvider>
              <ConditionalProvider subjects={conditionals}>
                <ConversationDetails
                  handleSubmit={handleSubmit}
                  loading={mutation.isPending}
                />
              </ConditionalProvider>
            </ConditionalTypeProvider>
          </PanelSlider>
        )}
      </AnimatePresence>
      {showAutoGenerate && (
        <Modal
          header="Auto generate workflow"
          handleClose={handleAutoGenerateClose}>
          <Flex as="form" col pad="true" onSubmit={handleAutoGenerateSubmit}>
            <Label htmlFor="auto-generate-workflow-input">
              Describe the workflow you want to generate
            </Label>
            <TextArea
              id="auto-generate-workflow-input"
              value={autoGeneratePrompt}
              onChange={event => setAutoGeneratePrompt(event.target.value)}
              placeholder="Enter workflow requirements or a short prompt"
              rows={8}
            />
            <Flex right mt>
              <Button
                type="submit"
                variant="primary"
                loading={autoGenerateMutation.isPending}
                disabled={!autoGeneratePrompt.trim() || autoGenerateMutation.isPending}>
                Submit
              </Button>
            </Flex>
          </Flex>
        </Modal>
      )}
      <PermissionFilter can="create conversations">
        <Button
          variant="primary"
          icon="plus"
          onClick={() => setShowCreate(true)}
          aria-label="Create workflow">
          New Workflow
        </Button>
        <Button
          variant="primary"
          icon="sparkles"
          onClick={() => setShowAutoGenerate(true)}
          aria-label="Auto generate workflow">
          Auto Generate Workflow
        </Button>
      </PermissionFilter>
    </>
  );
};

export default CreateConversation;
